// ── <RouteErrorBoundary /> — friendly recovery card for route crashes.
// Wraps the routed <Outlet /> so that if ANY page throws during render
// the child sees a warm "Something went wrong" card with Reload / Go
// home — never a blank white screen. Reset on navigation via `resetKey`
// so moving to another route clears a previous crash automatically.
import React from "react";
import { trackEvent } from "@/lib/analytics";

export default class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { crashed: false, message: "" };
  }

  static getDerivedStateFromError(err) {
    return { crashed: true, message: String(err?.message || err || "").slice(0, 160) };
  }

  componentDidCatch(err, info) {
    try {
      trackEvent("route_render_crashed", {
        path: (typeof window !== "undefined" ? window.location.pathname : "").slice(0, 80),
        message: String(err?.message || err).slice(0, 120),
        stack: String(info?.componentStack || "").slice(0, 200),
      });
    } catch { /* analytics must never mask the crash */ }
  }

  componentDidUpdate(prevProps) {
    // Navigating to a new route clears the crashed state so the next
    // page gets a clean render attempt.
    if (this.state.crashed && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ crashed: false, message: "" });
    }
  }

  render() {
    if (!this.state.crashed) return this.props.children;
    return (
      <div
        data-testid="route-error-card"
        className="min-h-[60vh] flex items-center justify-center px-4 py-16"
      >
        <div className="max-w-md w-full text-center rounded-3xl border-4 border-slate-900 bg-white p-7 shadow-[8px_8px_0_0_rgba(15,23,42,1)]">
          <span aria-hidden className="text-5xl">🧭</span>
          <h1 className="mt-3 font-heading text-2xl font-bold text-slate-900">
            Something went wrong loading this page
          </h1>
          <p className="mt-2 text-sm font-medium text-slate-600">
            Don&apos;t worry — your progress is safe. Try again, or head back home.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <button
              type="button"
              data-testid="route-error-reload"
              onClick={() => window.location.reload()}
              className="inline-flex items-center justify-center h-12 px-6 rounded-full border-[3px] border-slate-900 bg-emerald-400 font-bold text-sm shadow-[0_5px_0_0_rgba(15,23,42,1)] hover:-translate-y-0.5 active:translate-y-1 active:shadow-none transition-all"
            >
              Reload
            </button>
            <a
              href="/"
              data-testid="route-error-home"
              className="inline-flex items-center justify-center h-12 px-6 rounded-full border-[3px] border-slate-900 bg-white font-bold text-sm shadow-[0_5px_0_0_rgba(15,23,42,1)] hover:-translate-y-0.5 active:translate-y-1 active:shadow-none transition-all"
            >
              Go home
            </a>
          </div>
        </div>
      </div>
    );
  }
}
